import { PAYMENT_PROVIDER } from 'types/enums';

const KNOWN_PROVIDERS = Object.values(PAYMENT_PROVIDER) as string[];

const isKnownProvider = (value: string): value is PAYMENT_PROVIDER =>
  KNOWN_PROVIDERS.includes(value);

/**
 * Card providers this build will honour. The backend may offer more; any
 * provider absent from this list is never selected.
 */
export const getEnabledProviders = (): PAYMENT_PROVIDER[] => {
  const parsed = (process.env.REACT_APP_ENABLED_PROVIDERS ?? '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(isKnownProvider);

  return parsed.length > 0 ? parsed : [PAYMENT_PROVIDER.PAYSTACK];
};

export const isProviderEnabled = (provider?: PAYMENT_PROVIDER): boolean =>
  !!provider && getEnabledProviders().includes(provider);

/**
 * The provider used when no other choice is available. Never returns a
 * disabled provider, even if REACT_APP_CARD_HANDLER names one.
 */
export const getDefaultProvider = (): PAYMENT_PROVIDER => {
  const configured = process.env.REACT_APP_CARD_HANDLER as
    | PAYMENT_PROVIDER
    | undefined;

  if (configured && isProviderEnabled(configured)) {
    return configured;
  }

  const enabled = getEnabledProviders();
  return enabled.includes(PAYMENT_PROVIDER.PAYSTACK)
    ? PAYMENT_PROVIDER.PAYSTACK
    : enabled[0];
};

/**
 * Narrows a proposed provider to one this build will honour.
 */
export const resolveProvider = (
  candidate?: PAYMENT_PROVIDER,
): PAYMENT_PROVIDER =>
  isProviderEnabled(candidate) ? (candidate as PAYMENT_PROVIDER) : getDefaultProvider();

/**
 * A checkout on FirstCheckout is a customs payment. Its provider is locked
 * at initiation and the card BIN lookup never moves it.
 */
export const isCustomsProvider = (provider?: string): boolean =>
  provider === PAYMENT_PROVIDER.FIRSTCHEKOUT;

/**
 * Whether a checkout runs as a customs payment, which OPay cannot carry.
 * Customs details alone count while the provider is still undecided, so OPay
 * never shows and then vanishes; once the checkout falls back off
 * FirstCheckout it is an ordinary payment (the details still travel as
 * metadata) and OPay is offered.
 */
export const isCustomsCheckout = (
  hasCustomsDetails: boolean,
  provider?: string,
): boolean => hasCustomsDetails && (!provider || isCustomsProvider(provider));

/**
 * The provider a checkout starts on, from those the backend offered in order.
 * FirstCheckout is taken for a customs payment whenever it is offered and
 * enabled, and is never taken for any other payment.
 */
export const selectInitialProvider = (
  offered: string[],
  isCustomsPayment: boolean,
): PAYMENT_PROVIDER => {
  const available = offered
    .filter(isKnownProvider)
    .filter((provider) => isProviderEnabled(provider));

  if (isCustomsPayment && available.includes(PAYMENT_PROVIDER.FIRSTCHEKOUT)) {
    return PAYMENT_PROVIDER.FIRSTCHEKOUT;
  }

  return (
    available.find((provider) => !isCustomsProvider(provider)) ??
    getDefaultProvider()
  );
};
